import React, { useEffect, useMemo, useState } from 'react';
import type { AppContext } from '../app';
import type { DirectorState } from '../director/Director';
import { signEnvelope } from '../envelope/Envelope';
import { renderSurface } from './surfaces/renderer';
import { useShell } from './shells/ShellProvider';

interface Props {
  ctx: AppContext;
}

/**
 * SurfaceStack — subscribes to the director, renders the stack in 3D
 * perspective with depth-based diminish. Every action call back into the
 * director using one of the six gestures, and material side-effects
 * (envelope signing, storage writes, sync broadcasts) flow through the
 * AppContext.
 */
export function SurfaceStack({ ctx }: Props): JSX.Element {
  const [dirState, setDirState] = useState<DirectorState>(ctx.director.state());
  const { shell } = useShell();

  useEffect(() => {
    return ctx.director.subscribe(setDirState);
  }, [ctx]);

  // First mount: if the stack is empty, bridge into the active shell's
  // Home if it defines one (Phase 73 full); otherwise into the saved
  // role surface; otherwise into the hub role picker.
  useEffect(() => {
    if (dirState.active !== null) return;
    let target = 'hub';
    let reason = 'initial-mount';
    if (shell.Home && ctx.surfaces.get('shell-home')) {
      target = 'shell-home';
      reason = `shell-${shell.id}`;
    } else {
      try {
        const role = localStorage.getItem('alds-role');
        const map: Record<string, string> = {
          caller: 'env-intake',
          dispatcher: 'env-board',
          responder: 'env-roster',
          supervisor: 'env-audit',
        };
        if (role && map[role] && ctx.surfaces.get(map[role])) {
          target = map[role];
          reason = `resume-${role}`;
        }
      } catch { /* localStorage not available; fall through to hub */ }
    }
    ctx.director.bridge(target, reason);
  }, [dirState.active, ctx, shell.id, shell.Home]);

  // Signing action that surfaces can call. Signs via the AppContext identity
  // (which is RemoteSignerIdentity → keyring SharedWorker in production),
  // then writes to both local storage and the sync provider so peers see it.
  const signAndStore = useMemo(() => async (
    surfaceId: string,
    contentType: string,
    payload: unknown,
  ) => {
    const def = ctx.surfaces.get(surfaceId);
    if (!def) throw new Error(`Unknown surface ${surfaceId}`);
    const context = { environment: def.environment, purpose: surfaceId };
    const envelope = await signEnvelope({
      identity: ctx.identity,
      context,
      contentType,
      payload: btoa(JSON.stringify(payload)),
    });
    const streamId = `stream:${surfaceId}`;
    // Ensure the stream is open in the sync provider, then append.
    // open() is idempotent in both LocalSyncProvider and OrbitDBSyncProvider.
    await ctx.sync.open(streamId, context);
    // Write to local storage (encrypted at rest) and to the sync log
    // (replicated to peers). The order matters: storage first, so if the
    // sync write fails we still have the envelope persisted locally.
    await ctx.storage.putEnvelope(streamId, envelope);
    await ctx.sync.append(streamId, envelope);
    return envelope;
  }, [ctx]);

  // Build the visible stack (last 4 entries, deepest first)
  const stack = dirState.stack.slice(-4);

  /**
   * U3 — dev-only debug label. Hide unless `?debug=1` or
   * `localStorage['alds-dev']` is set. Previously visible on all
   * surfaces on desktop as "surface stack · N deep" which is engineer
   * info that doesn't help end users.
   */
  const debugMode = (() => {
    try {
      if (typeof window === 'undefined') return false;
      if (window.location.search.includes('debug=1')) return true;
      if (localStorage.getItem('alds-dev') !== null) return true;
      return false;
    } catch { return false; }
  })();

  return (
    <section className="stack-region">
      <style>{styles}</style>
      {debugMode && (
        <div className="stage-label">
          surface stack · {dirState.stack.length} deep
        </div>
      )}

      {stack.map((surfaceId, i) => {
        const def = ctx.surfaces.get(surfaceId);
        if (!def) return null;
        const depth = stack.length - 1 - i;
        const cls = depth === 0 ? 'surf-active'
          : depth === 1 ? 'surf-d1'
          : depth === 2 ? 'surf-d2'
          : 'surf-d3';

        return (
          <article key={`${surfaceId}-${i}-${dirState.stack.length}`} className={`surface schematic-frame ${cls}`}>
            <div className="surface-id">
              <div className="left">
                {depth === 0 && <span className="pulse-dot" />}
                surface · {surfaceId} · v{def.version}
              </div>
              <span className="class-tag">{def.surfaceClass}</span>
            </div>

            {depth === 0 && renderSurface(def, {
              ctx,
              director: ctx.director,
              signAndStore,
            })}
          </article>
        );
      })}
    </section>
  );
}

const styles = `
.stack-region {
  position: relative;
  height: 100%;
  min-height: 560px;
  perspective: 1800px;
  perspective-origin: 50% 30%;
}

.stage-label {
  position: absolute;
  top: -18px; left: 0;
  font-size: 9px;
  letter-spacing: 0.3em;
  color: var(--ink-dim);
  text-transform: uppercase;
}
@media (max-width: 700px) {
  .stage-label { display: none; }
  /* Keep .stack-region min-height — without it the absolute-positioned
   * .surface children collapse to 0 and the Inspector panels show
   * through. The stage-label hide is purely cosmetic. */
}
.stage-label::before { content: '◢ '; color: var(--phos); }

.surface {
  position: absolute;
  inset: 0;
  background: var(--bg-2);
  padding: 32px;
  transition: transform 600ms var(--surface-ease),
              opacity 600ms var(--surface-ease),
              filter 600ms var(--surface-ease);
  transform-style: preserve-3d;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.surface-id {
  font-size: 9px;
  letter-spacing: 0.3em;
  color: var(--phos-dim);
  text-transform: uppercase;
  margin-bottom: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.surface-id .left { display: flex; align-items: center; gap: 12px; }
.surface-id .class-tag {
  padding: 2px 8px;
  border: 1px solid var(--phos-dim);
  color: var(--phos);
  white-space: nowrap;   /* U1.2 — prevent "PERSISTENT" from wrapping into "PERSISTE NT" on mobile */
}

/* U1.3a — on mobile the surface-id row is dev chrome (debug info about
 * the surface's id + classification) that mostly steals viewport space
 * from the actual content. Hide it entirely on phones. Operators who
 * want it can read the surface stack indicator in the ambient rail
 * instead. */
@media (max-width: 700px) {
  .surface-id { display: none; }
  .surface { padding: 16px; }
}

.surf-active {
  transform: translateZ(0);
  opacity: 1;
  z-index: 10;
  /* Phase U0.5 — slide-in animation on every fresh mount. CSS
   * transition (above) only fires when an existing element's
   * transform CHANGES, so freshly-mounted surfaces would otherwise
   * snap into place with no motion. STP captures confirmed the
   * pre-animation state (6 identical frames during a tab tap).
   * Keep the duration modest (220ms) so the navigation still feels
   * snappy on mobile. */
  animation: sass-surface-slide-in 380ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

@keyframes sass-surface-slide-in {
  from {
    transform: translate3d(28px, 0, 0);
    opacity: 0;
  }
  to {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .surf-active { animation: none; }
  .surface { transition: none; }
}
.surf-d1 {
  transform: translateZ(-60px) translateY(-28px) scale(0.96);
  opacity: 0.35;
  filter: blur(0.5px);
  pointer-events: none;
  z-index: 9;
}
.surf-d2 {
  transform: translateZ(-120px) translateY(-50px) scale(0.92);
  opacity: 0.15;
  filter: blur(1px);
  pointer-events: none;
  z-index: 8;
}
.surf-d3 {
  transform: translateZ(-180px) translateY(-66px) scale(0.88);
  opacity: 0.07;
  filter: blur(2px);
  pointer-events: none;
  z-index: 7;
}
`;
